import type { Ref } from "react";
import { cn } from "@/utils/cn";
type PatientListRow = {
  firstName: string;
  lastName: string;
  officeName?: string;
};

type PatientListPickerContentProps<T extends PatientListRow> = {
  patients: T[];
  getKey: (p: T) => string;
  onSelect: (p: T) => void;
  firstItemRef?: Ref<HTMLButtonElement>;
  className?: string;
  /** @default "Select Patient" */
  title?: string;
  /** @default "Select the patient record you would like to manage" */
  description?: string;
  /**
   * When true, item is the current patient (already active on switch-user): listed first,
   * badged "Current", and non-interactive.
   */
  isItemDisabled?: (p: T) => boolean;
};

/**
 * The patient chooser list used on login; reuse from the account menu (switch patient).
 */
export function PatientListPickerContent<T extends PatientListRow>({
  patients,
  getKey,
  onSelect,
  firstItemRef,
  className,
  title = "Select Patient",
  description = "Select the patient record you would like to manage",
  isItemDisabled,
}: PatientListPickerContentProps<T>) {
  // Current patient first so it isn't lost mid-list on a phone. The API order is unsorted, so the
  // rest go by facility (the current patient's facility first), then last name, then first name.
  const current = isItemDisabled == null ? [] : patients.filter((p) => isItemDisabled(p));
  const others = isItemDisabled == null ? patients : patients.filter((p) => !isItemDisabled(p));
  const currentOffice = current[0]?.officeName ?? null;
  const ordered: T[] = [
    ...current,
    ...[...others].sort((a, b) => comparePatients(a, b, currentOffice)),
  ];
  const firstFocusableIndex: number =
    isItemDisabled == null
      ? 0
      : ordered.findIndex((p) => !isItemDisabled(p));

  return (
    <div className={className}>
      <h3 className="text-xl font-extrabold text-healthsource-dark">{title}</h3>
      <p className="mt-1 text-sm font-semibold text-healthsource-gray">{description}</p>
      <ul className="mt-3 list-none space-y-2 p-0">
        {ordered.map((p, index) => {
          const disabled = isItemDisabled?.(p) === true;
          return (
            <li key={getKey(p)}>
              {/* The whole row is the button so the touch target is the full card, at least 44px. */}
              <button
                ref={
                  (firstFocusableIndex < 0
                    ? false
                    : !disabled && index === firstFocusableIndex) ? firstItemRef
                    : undefined
                }
                type="button"
                className={cn(
                  "flex min-h-11 w-full items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-healthsource-navy",
                  disabled
                    ? "cursor-default border-healthsource-navy bg-[var(--hs-tint)]"
                    : "border-[var(--hs-line)] hover:border-healthsource-navy hover:bg-[var(--hs-tint)]"
                )}
                disabled={disabled}
                aria-current={disabled ? "true" : undefined}
                onClick={() => {
                  if (!disabled) onSelect(p);
                }}
              >
                <span className="min-w-0 flex-1">
                  <span className="block font-bold text-healthsource-dark">
                    {p.firstName} {p.lastName}
                  </span>
                  {p.officeName && (
                    <span className="mt-0.5 block text-sm text-healthsource-gray">
                      {p.officeName}
                    </span>
                  )}
                </span>
                {disabled && (
                  <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-healthsource-navy px-2.5 py-1 text-[13px] font-bold text-white">
                    <i className="fas fa-check text-[11px]" aria-hidden />
                    Current
                  </span>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function compareText(a: string | undefined, b: string | undefined): number {
  return (a ?? "").localeCompare(b ?? "", undefined, { sensitivity: "base" });
}

function comparePatients(
  a: PatientListRow,
  b: PatientListRow,
  currentOffice: string | null
): number {
  if (currentOffice != null) {
    const aHome = compareText(a.officeName, currentOffice) === 0;
    const bHome = compareText(b.officeName, currentOffice) === 0;
    if (aHome !== bHome) return aHome ? -1 : 1;
  }
  return (
    compareText(a.officeName, b.officeName) ||
    compareText(a.lastName, b.lastName) ||
    compareText(a.firstName, b.firstName)
  );
}
